'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

interface JobListing {
  id: string;
  title: string;
  description: string;
  niche: string;
  budget: string;
  duration: string;
  requirements: string[];
  campaign_start: string | null;
  campaign_end: string | null;
  status: string;
  views_count: number;
  created_at: string;
  pay_type: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  contract_type: string | null;
  location_type: string | null;
  business_profiles: {
    company_name: string;
    industry: string;
    location: string;
    logo_url: string;
  } | null;
}

const CATEGORIES = ['All', 'Beauty & Skincare', 'Fashion', 'Health & Wellness', 'Tech', 'Home & Kitchen', 'Food & Snacks', 'Lifestyle & Gifts'];
const BUDGETS = ['All', 'Under $2,000', '$2,000–$5,000', '$5,000–$10,000', '$10,000+'];
const CONTRACT_TYPES = ['All', 'Per Job / Booking', 'Permanent', 'Temporary', 'Freelance', 'Part-Time', 'Full-Time'];
const SORT_OPTIONS = [
  { label: 'Newest First', value: 'newest' },
  { label: 'Oldest First', value: 'oldest' },
  { label: 'Most Views', value: 'views' },
  { label: 'Budget: High to Low', value: 'budget_desc' },
];

const ACCENT_COLORS = ['bg-theme-01', 'bg-theme-02', 'bg-theme-03', 'bg-theme-04', 'bg-theme-purple'];

function getAccent(id: string) {
  const idx = id.charCodeAt(0) % ACCENT_COLORS.length;
  return ACCENT_COLORS[idx];
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function parseBudgetMin(budget: string): number {
  const match = budget.match(/\$?([\d,]+)/);
  if (!match) return 0;
  return parseInt(match[1].replace(/,/g, ''), 10);
}

export default function JobBoardPage() {
  const [jobs, setJobs] = useState<JobListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [budget, setBudget] = useState('All');
  const [contractFilter, setContractFilter] = useState('All');
  const [sort, setSort] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const supabase = createClient();
    const { data, error } = await supabase
      .from('job_listings')
      .select(`
        id, title, description, niche, budget, duration,
        requirements, campaign_start, campaign_end, status,
        views_count, created_at,
        pay_type, salary_min, salary_max, salary_currency,
        contract_type, location_type,
        business_profiles (company_name, industry, location, logo_url)
      `)
      .eq('status', 'active')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setJobs(data as JobListing[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const CONTRACT_TYPE_MAP: Record<string, string> = {
    per_job_booking: 'Per Job / Booking',
    permanent: 'Permanent',
    temporary: 'Temporary',
    freelance: 'Freelance',
    part_time: 'Part-Time',
    full_time: 'Full-Time',
    contract: 'Fixed-Term Contract',
  };

  const PAY_TYPE_LABEL: Record<string, string> = {
    hourly: '/hr',
    per_stream: '/stream',
    per_job: '/job',
    monthly: '/mo',
    yearly: '/yr',
    negotiable: '',
  };

  const filtered = jobs
    .filter((j) => {
      if (search) {
        const q = search.toLowerCase();
        const matchTitle = j.title.toLowerCase().includes(q);
        const matchNiche = j.niche.toLowerCase().includes(q);
        const matchCompany = j.business_profiles?.company_name.toLowerCase().includes(q) ?? false;
        if (!matchTitle && !matchNiche && !matchCompany) return false;
      }
      if (category !== 'All' && j.niche !== category) return false;
      if (budget !== 'All') {
        const min = parseBudgetMin(j.budget);
        if (budget === 'Under $2,000' && min >= 2000) return false;
        if (budget === '$2,000–$5,000' && (min < 2000 || min >= 5000)) return false;
        if (budget === '$5,000–$10,000' && (min < 5000 || min >= 10000)) return false;
        if (budget === '$10,000+' && min < 10000) return false;
      }
      if (contractFilter !== 'All') {
        const mapped = j.contract_type ? CONTRACT_TYPE_MAP[j.contract_type] : null;
        if (mapped !== contractFilter) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sort === 'newest') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sort === 'oldest') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sort === 'views') return (b.views_count ?? 0) - (a.views_count ?? 0);
      if (sort === 'budget_desc') return parseBudgetMin(b.budget) - parseBudgetMin(a.budget);
      return 0;
    });

  return (
    <main className="font-montserrat bg-white text-dark overflow-x-hidden min-h-screen">
      <Navbar />

      {/* Hero Header */}
      <section className="pt-28 pb-12 bg-neutral-01">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="mb-4">
                <span
                  className="bg-theme-03 text-dark text-[13px] font-bold px-4 py-1.5 rounded-2xl inline-block"
                  style={{ transform: 'rotate(-2deg)', display: 'inline-block' }}
                >
                  Live Now
                </span>
              </div>
              <h1 className="text-[40px] md:text-[52px] font-bold text-dark leading-tight mb-3">
                Find your next<br />live commerce gig
              </h1>
              <p className="text-[17px] text-dark/60 max-w-[480px] leading-relaxed">
                Brands are actively hiring TikTok Shop live sellers. Browse, filter, and apply in minutes.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="bg-white rounded-2xl px-5 py-3 border border-neutral-02 text-center">
                <p className="text-[24px] font-bold text-dark leading-none">{jobs.length}</p>
                <p className="text-[12px] text-dark/50 font-medium mt-0.5">Open Jobs</p>
              </div>
              <div className="bg-theme-02 rounded-2xl px-5 py-3 text-center">
                <p className="text-[24px] font-bold text-dark leading-none">
                  {jobs.filter(j => {
                    const diff = Date.now() - new Date(j.created_at).getTime();
                    return diff < 86400000 * 3;
                  }).length}
                </p>
                <p className="text-[12px] text-dark/60 font-medium mt-0.5">New This Week</p>
              </div>
            </div>
          </div>

          {/* Search + Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-dark/30" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                type="text"
                placeholder="Search by title, niche, or brand..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-white rounded-xl pl-11 pr-4 py-3.5 text-[14px] font-medium text-dark outline-none focus:ring-2 focus:ring-theme-01 transition-all border border-neutral-02"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-5 py-3.5 rounded-xl font-semibold text-[14px] border transition-all duration-200 ${
                showFilters ? 'bg-dark text-white border-dark' : 'bg-white text-dark border-neutral-02 hover:border-dark'
              }`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Filters
              {(category !== 'All' || budget !== 'All' || contractFilter !== 'All') && (
                <span className="w-5 h-5 bg-theme-01 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                  {(category !== 'All' ? 1 : 0) + (budget !== 'All' ? 1 : 0) + (contractFilter !== 'All' ? 1 : 0)}
                </span>
              )}
            </button>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-white rounded-xl px-4 py-3.5 text-[14px] font-semibold text-dark outline-none border border-neutral-02 focus:ring-2 focus:ring-theme-01 transition-all cursor-pointer"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 bg-white rounded-2xl p-5 border border-neutral-02">
              <div className="flex flex-col gap-5">
                <div>
                  <p className="text-[12px] font-bold text-dark/50 uppercase tracking-wide mb-3">Category / Niche</p>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map(c => (
                      <button
                        key={c}
                        onClick={() => setCategory(c)}
                        className={`text-[13px] font-semibold px-4 py-1.5 rounded-full transition-all duration-200 ${
                          category === c ? 'bg-dark text-white' : 'bg-neutral-01 text-dark hover:bg-neutral-02'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-5">
                  <div className="flex-1">
                    <p className="text-[12px] font-bold text-dark/50 uppercase tracking-wide mb-3">Budget Range</p>
                    <div className="flex flex-wrap gap-2">
                      {BUDGETS.map(b => (
                        <button
                          key={b}
                          onClick={() => setBudget(b)}
                          className={`text-[13px] font-semibold px-4 py-1.5 rounded-full transition-all duration-200 ${
                            budget === b ? 'bg-dark text-white' : 'bg-neutral-01 text-dark hover:bg-neutral-02'
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-[12px] font-bold text-dark/50 uppercase tracking-wide mb-3">Contract Type</p>
                    <div className="flex flex-wrap gap-2">
                      {CONTRACT_TYPES.map(ct => (
                        <button
                          key={ct}
                          onClick={() => setContractFilter(ct)}
                          className={`text-[13px] font-semibold px-4 py-1.5 rounded-full transition-all duration-200 ${
                            contractFilter === ct ? 'bg-dark text-white' : 'bg-neutral-01 text-dark hover:bg-neutral-02'
                          }`}
                        >
                          {ct}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {(category !== 'All' || budget !== 'All' || contractFilter !== 'All') && (
                <button
                  onClick={() => { setCategory('All'); setBudget('All'); setContractFilter('All'); }}
                  className="mt-4 text-[13px] font-semibold text-dark/50 hover:text-dark transition-colors underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="py-12 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Results count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-[14px] text-dark/50 font-medium">
              {loading ? 'Loading jobs...' : `${filtered.length} job${filtered.length !== 1 ? 's' : ''} found`}
            </p>
            {(search || category !== 'All' || budget !== 'All') && !loading && (
              <button
                onClick={() => { setSearch(''); setCategory('All'); setBudget('All'); }}
                className="text-[13px] font-semibold text-dark/50 hover:text-dark transition-colors"
              >
                Clear search
              </button>
            )}
          </div>

          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-neutral-01 rounded-[24px] p-6 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 bg-neutral-02 rounded-2xl" />
                    <div className="flex-1">
                      <div className="h-4 bg-neutral-02 rounded-full w-3/4 mb-2" />
                      <div className="h-3 bg-neutral-02 rounded-full w-1/2" />
                    </div>
                  </div>
                  <div className="flex gap-2 mb-4">
                    <div className="h-7 bg-neutral-02 rounded-full w-24" />
                    <div className="h-7 bg-neutral-02 rounded-full w-20" />
                    <div className="h-7 bg-neutral-02 rounded-full w-28" />
                  </div>
                  <div className="h-3 bg-neutral-02 rounded-full w-full mb-2" />
                  <div className="h-3 bg-neutral-02 rounded-full w-4/5" />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-neutral-01 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" stroke="#9A9090" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="text-[20px] font-bold text-dark mb-2">No jobs found</h3>
              <p className="text-[15px] text-dark/50 mb-6">Try adjusting your filters or search terms.</p>
              <button
                onClick={() => { setSearch(''); setCategory('All'); setBudget('All'); }}
                className="font-bold text-[14px] text-white bg-dark px-6 py-3 rounded-pill hover:bg-opacity-80 transition-all"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Job Cards */}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {filtered.map((job) => {
                const accent = getAccent(job.id);
                const company = job.business_profiles;
                const isNew = Date.now() - new Date(job.created_at).getTime() < 86400000 * 2;

                return (
                  <div
                    key={job.id}
                    className="bg-neutral-01 rounded-[24px] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 ${accent} rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                          {company?.logo_url ? (
                            <img src={company.logo_url} alt={`${company.company_name} logo`} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-[16px] font-bold text-dark">
                              {company?.company_name?.charAt(0) ?? '?'}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-dark text-[15px] leading-tight line-clamp-1">{job.title}</p>
                          <p className="text-dark/50 text-[13px] mt-0.5">{company?.company_name ?? 'Unknown Brand'}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        {isNew && (
                          <span className="text-[11px] font-bold text-white bg-dark px-3 py-1 rounded-full">
                            New
                          </span>
                        )}
                        {job.contract_type && (
                          <span className="text-[10px] font-semibold text-dark/60 bg-white px-2.5 py-0.5 rounded-full border border-neutral-02">
                            {CONTRACT_TYPE_MAP[job.contract_type] || job.contract_type}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    {job.description && (
                      <p className="text-[13px] text-dark/60 leading-relaxed mb-4 line-clamp-2">{job.description}</p>
                    )}

                    {/* Details */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      {job.budget && (
                        <div className="flex items-center gap-1.5 text-[13px] font-semibold text-dark">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" fill="currentColor"/>
                          </svg>
                          {job.budget}
                          {job.pay_type && job.pay_type !== 'per_stream' && PAY_TYPE_LABEL[job.pay_type] && (
                            <span className="text-[11px] text-dark/40 font-normal">{PAY_TYPE_LABEL[job.pay_type]}</span>
                          )}
                        </div>
                      )}
                      {job.duration && (
                        <div className="flex items-center gap-1.5 text-[13px] text-dark/60">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.5 5H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
                          </svg>
                          {job.duration}
                        </div>
                      )}
                      {job.location_type && (
                        <div className="flex items-center gap-1.5 text-[13px] text-dark/60">
                          {job.location_type === 'remote' ? '🌐' : job.location_type === 'on_site' ? '📍' : '🔀'}
                          <span>{job.location_type === 'remote' ? 'Remote' : job.location_type === 'on_site' ? 'On-Site' : 'Hybrid'}</span>
                        </div>
                      )}
                      {!job.location_type && company?.location && (
                        <div className="flex items-center gap-1.5 text-[13px] text-dark/60">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                          </svg>
                          {company.location}
                        </div>
                      )}
                      {job.views_count > 0 && (
                        <div className="flex items-center gap-1.5 text-[13px] text-dark/40">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                          </svg>
                          {job.views_count} views
                        </div>
                      )}
                    </div>

                    {/* Niche tag */}
                    {job.niche && (
                      <div className="flex flex-wrap gap-1.5 mb-5">
                        <span className="text-[11px] font-medium text-dark/60 bg-white px-3 py-1 rounded-full border border-neutral-02">
                          {job.niche}
                        </span>
                        {job.requirements?.slice(0, 2).map((req, ri) => (
                          <span key={ri} className="text-[11px] font-medium text-dark/60 bg-white px-3 py-1 rounded-full border border-neutral-02 line-clamp-1 max-w-[140px] truncate">
                            {req}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-neutral-02">
                      <span className="text-[12px] text-dark/40 font-medium">Posted {timeAgo(job.created_at)}</span>
                      <Link
                        href={`/jobs/${job.id}`}
                        className="font-bold text-[13px] text-dark group-hover:text-theme-01 transition-colors duration-200 flex items-center gap-1"
                      >
                        View & Apply
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
